/**
 * taskpane.ts - Main task pane logic for WTools Mac
 *
 * Handles Office.js initialization, UI event handlers, and
 * coordinates with feature modules for PowerPoint operations.
 */

import { getCurrentFillColor, setCurrentFillColor, getCurrentTextColor, setCurrentTextColor, getCurrentFont, setCurrentFont } from '../ui/ColorPicker';
import { adjustMargins, removeMargins } from '../features/text/Margins';

// Office and PowerPoint types are available globally via office-js package loaded in HTML

/**
 * Initialize the add-in when Office is ready
 */
Office.onReady((info: { host: Office.HostType | null; platform: Office.PlatformType | null }) => {
  if (info.host === Office.HostType.PowerPoint) {
    console.info('WTools for Mac initialized');
    initializeUI();
    loadSavedSettings();
  }
});

/**
 * Initialize all UI event handlers
 */
function initializeUI(): void {
  // Color picker handlers
  const fillColorInput = document.getElementById('fillColor') as HTMLInputElement;
  const textColorInput = document.getElementById('textColor') as HTMLInputElement;
  const fontSelect = document.getElementById('defaultFont') as HTMLSelectElement;

  if (fillColorInput) {
    fillColorInput.addEventListener('input', handleFillColorChange);
    fillColorInput.addEventListener('change', handleFillColorChange);
  }

  if (textColorInput) {
    textColorInput.addEventListener('input', handleTextColorChange);
    textColorInput.addEventListener('change', handleTextColorChange);
  }

  if (fontSelect) {
    fontSelect.addEventListener('change', handleFontChange);
  }

  // Quick action buttons
  const btnInsertTextbox = document.getElementById('btnInsertTextbox');
  const btnInsertTable = document.getElementById('btnInsertTable');
  const btnInsertRectangle = document.getElementById('btnInsertRectangle');
  const btnInsertOval = document.getElementById('btnInsertOval');

  if (btnInsertTextbox) {
    btnInsertTextbox.addEventListener('click', handleInsertTextbox);
  }

  if (btnInsertTable) {
    btnInsertTable.addEventListener('click', handleInsertTable);
  }

  if (btnInsertRectangle) {
    btnInsertRectangle.addEventListener('click', () => handleInsertShape('rectangle'));
  }

  if (btnInsertOval) {
    btnInsertOval.addEventListener('click', () => handleInsertShape('ellipse'));
  }

  // Margin buttons
  const btnMarginIncrease = document.getElementById('btnMarginIncrease');
  const btnMarginDecrease = document.getElementById('btnMarginDecrease');
  const btnMarginRemove = document.getElementById('btnMarginRemove');

  if (btnMarginIncrease) {
    btnMarginIncrease.addEventListener('click', () => handleAdjustMargins(5));
  }

  if (btnMarginDecrease) {
    btnMarginDecrease.addEventListener('click', () => handleAdjustMargins(-5));
  }

  if (btnMarginRemove) {
    btnMarginRemove.addEventListener('click', handleRemoveMargins);
  }

  // AI settings handlers
  const btnSaveApiKey = document.getElementById('btnSaveApiKey');
  const btnToggleApiKey = document.getElementById('btnToggleApiKey');

  if (btnSaveApiKey) {
    btnSaveApiKey.addEventListener('click', handleSaveApiKey);
  }

  if (btnToggleApiKey) {
    btnToggleApiKey.addEventListener('click', handleToggleApiKeyVisibility);
  }
}

/**
 * Load saved settings from localStorage
 */
function loadSavedSettings(): void {
  // Load color settings
  const savedFillColor = getCurrentFillColor();
  const savedTextColor = getCurrentTextColor();
  const savedFont = getCurrentFont();

  const fillColorInput = document.getElementById('fillColor') as HTMLInputElement;
  const textColorInput = document.getElementById('textColor') as HTMLInputElement;
  const fontSelect = document.getElementById('defaultFont') as HTMLSelectElement;
  const fillColorValue = document.getElementById('fillColorValue');
  const textColorValue = document.getElementById('textColorValue');

  if (fillColorInput && savedFillColor) {
    fillColorInput.value = savedFillColor;
    if (fillColorValue) {
      fillColorValue.textContent = savedFillColor;
    }
  }

  if (textColorInput && savedTextColor) {
    textColorInput.value = savedTextColor;
    if (textColorValue) {
      textColorValue.textContent = savedTextColor;
    }
  }

  if (fontSelect && savedFont) {
    fontSelect.value = savedFont;
  }

  // Load API key settings
  const savedApiKey = getApiKey();
  const savedModel = localStorage.getItem('wtools_ai_model');
  const apiKeyInput = document.getElementById('apiKey') as HTMLInputElement;
  const aiModelSelect = document.getElementById('aiModel') as HTMLSelectElement;

  if (apiKeyInput && savedApiKey) {
    apiKeyInput.value = savedApiKey;
  }

  if (aiModelSelect && savedModel) {
    aiModelSelect.value = savedModel;
  }
}

// ============================================
// Color Picker Event Handlers
// ============================================

function handleFillColorChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const color = input.value;
  setCurrentFillColor(color);

  const colorValue = document.getElementById('fillColorValue');
  if (colorValue) {
    colorValue.textContent = color.toUpperCase();
  }
}

function handleTextColorChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const color = input.value;
  setCurrentTextColor(color);

  const colorValue = document.getElementById('textColorValue');
  if (colorValue) {
    colorValue.textContent = color.toUpperCase();
  }
}

function handleFontChange(event: Event): void {
  const select = event.target as HTMLSelectElement;
  setCurrentFont(select.value);
}

// ============================================
// Shape Insertion Handlers
// ============================================

async function handleInsertTextbox(): Promise<void> {
  try {
    await PowerPoint.run(async (context: PowerPoint.RequestContext) => {
      const slide = context.presentation.getSelectedSlides().getItemAt(0);
      const shapes = slide.shapes;

      const fillColor = getCurrentFillColor();
      const textColor = getCurrentTextColor();
      const fontName = getCurrentFont();

      const textbox = shapes.addTextBox("Enter text here", {
        left: 100,
        top: 100,
        height: 72,
        width: 288
      });

      // Apply user-configured formatting
      textbox.textFrame.textRange.font.name = fontName;
      textbox.textFrame.textRange.font.size = 11;
      textbox.textFrame.textRange.font.color = textColor;
      textbox.textFrame.leftMargin = 7.2;
      textbox.textFrame.rightMargin = 7.2;
      textbox.textFrame.topMargin = 3.6;
      textbox.textFrame.bottomMargin = 3.6;

      await context.sync();
    });
  } catch (error) {
    console.error('Error inserting textbox:', error);
    showStatus('Failed to insert textbox', 'error');
  }
}

async function handleInsertTable(): Promise<void> {
  // For now, insert a default 3x3 table
  // Later: Use showTableInsertDialog() from Dialogs.ts
  try {
    await PowerPoint.run(async (context: PowerPoint.RequestContext) => {
      const slide = context.presentation.getSelectedSlides().getItemAt(0);
      const shapes = slide.shapes;

      // Note: shapes.addTable() requires API 1.8+
      // This is a placeholder - actual implementation may vary
      const table = shapes.addTable(3, 3, {
        left: 100,
        top: 150,
        height: 200,
        width: 500
      });

      await context.sync();
    });
  } catch (error) {
    console.error('Error inserting table:', error);
    showStatus('Failed to insert table. Table support requires API 1.8+', 'error');
  }
}

async function handleInsertShape(shapeType: string): Promise<void> {
  try {
    await PowerPoint.run(async (context: PowerPoint.RequestContext) => {
      const slide = context.presentation.getSelectedSlides().getItemAt(0);
      const shapes = slide.shapes;

      const fillColor = getCurrentFillColor();

      // Map shape type string to PowerPoint GeometricShapeType
      const shapeMap: Record<string, PowerPoint.GeometricShapeType> = {
        'rectangle': PowerPoint.GeometricShapeType.rectangle,
        'roundRectangle': PowerPoint.GeometricShapeType.roundRectangle,
        'ellipse': PowerPoint.GeometricShapeType.ellipse,
        'chevron': PowerPoint.GeometricShapeType.chevron,
      };

      const geometricType = shapeMap[shapeType] || PowerPoint.GeometricShapeType.rectangle;

      const shape = shapes.addGeometricShape(geometricType, {
        left: 100,
        top: 100,
        height: 72,
        width: 144
      });

      shape.fill.setSolidColor(fillColor);

      await context.sync();
    });
  } catch (error) {
    console.error('Error inserting shape:', error);
    showStatus('Failed to insert shape', 'error');
  }
}

// ============================================
// Margin Handlers
// ============================================

async function handleAdjustMargins(delta: number): Promise<void> {
  try {
    await adjustMargins(delta);
  } catch (error) {
    console.error('Error adjusting margins:', error);
    showStatus('Failed to adjust margins. Select shapes first.', 'error');
  }
}

async function handleRemoveMargins(): Promise<void> {
  try {
    await removeMargins();
  } catch (error) {
    console.error('Error removing margins:', error);
    showStatus('Failed to remove margins. Select shapes first.', 'error');
  }
}

// ============================================
// AI Settings Handlers
// ============================================

function handleSaveApiKey(): void {
  const apiKeyInput = document.getElementById('apiKey') as HTMLInputElement;
  const aiModelSelect = document.getElementById('aiModel') as HTMLSelectElement;

  if (apiKeyInput && apiKeyInput.value) {
    saveApiKey(apiKeyInput.value);

    if (aiModelSelect) {
      localStorage.setItem('wtools_ai_model', aiModelSelect.value);
    }

    showStatus('AI settings saved successfully!', 'success');
  } else {
    showStatus('Please enter an API key', 'error');
  }
}

function handleToggleApiKeyVisibility(): void {
  const apiKeyInput = document.getElementById('apiKey') as HTMLInputElement;
  const toggleIcon = document.getElementById('apiKeyToggleIcon');

  if (apiKeyInput && toggleIcon) {
    if (apiKeyInput.type === 'password') {
      apiKeyInput.type = 'text';
      toggleIcon.textContent = 'Hide';
    } else {
      apiKeyInput.type = 'password';
      toggleIcon.textContent = 'Show';
    }
  }
}

// ============================================
// API Key Storage Functions
// ============================================

function saveApiKey(key: string): void {
  localStorage.setItem('wtools_openai_key', key);
}

function getApiKey(): string | null {
  return localStorage.getItem('wtools_openai_key');
}

// ============================================
// Utility Functions
// ============================================

function showStatus(message: string, type: 'success' | 'error' | 'info'): void {
  const statusDiv = document.getElementById('apiKeyStatus');
  if (statusDiv) {
    statusDiv.textContent = message;
    statusDiv.className = `status-message ${type}`;
    statusDiv.style.display = 'block';

    // Auto-hide after 3 seconds
    setTimeout(() => {
      statusDiv.style.display = 'none';
    }, 3000);
  }
}

// Export functions for use by other modules
export {
  saveApiKey,
  getApiKey,
  showStatus
};
